import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { createSelector, Store } from '@ngrx/store';
import { filter, Observable, of, switchMap, take } from 'rxjs';

import { RoleCode, RoleName, routeNames } from '@ninety/platform/models';
import { RoleService } from '@ninety/platform/services/services/role.service';
import { selectCurrentUser } from '@ninety/platform/state/app-entities/users/users-state.selectors';
import { selectHasTier1Data } from '@ninety/platform/state/app-init/app-init.selectors';

const selectAuthorizedData = createSelector(selectHasTier1Data, selectCurrentUser, (hasTier1Data, user) => ({
  hasTier1Data,
  user,
}));

@Injectable({ providedIn: 'root' })
export class AuthGuard {
  private readonly permittedRoutesForAll = [routeNames.login, routeNames.signup, routeNames.acceptInvite];

  private readonly permittedRoutesForManagerOrManagee = [
    routeNames.directory,
    routeNames.feedback,
    routeNames.ac,
    routeNames.myfocus,
    routeNames.consent,
  ];

  private readonly permittedRoutesForObserver = [
    routeNames.directory,
    routeNames.ac,
    routeNames.myfocus,
    routeNames.consent,
  ];

  private readonly permittedRoutesForLite = [
    routeNames.feedback,
    routeNames.ac,
    routeNames.myfocus,
    routeNames.conversation,
    routeNames.oneOnOne,
    routeNames.discussions,
    routeNames.discussion,
    routeNames.consent,
  ];

  constructor(
    private readonly store: Store,
    private readonly router: Router
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | UrlTree | Observable<boolean | UrlTree> {
    return this.store.select(selectAuthorizedData).pipe(
      filter(({ hasTier1Data }) => hasTier1Data),
      take(1),
      switchMap(({ user }) => {
        const routes = route.url.map(r => r.path);

        if (this.permittedRoutesForAll.includes(routes?.[0]) || routes?.length === 0) return of(true);

        const userRole = this.getUserRoleName(user.roleCode);
        if (routes.includes('partner-hub')) return of(true);

        if (userRole === RoleName.lite && routes?.length !== 0) {
          const permittedRoutesForLite = routes.some(r => this.permittedRoutesForLite.includes(r));
          if (!permittedRoutesForLite) this.router.navigateByUrl('/home');
          return of(true);
        }

        const userHasTeams = RoleService.minRoleName.admin(userRole) || user?.teams?.length > 0;
        if (!userHasTeams && routes?.length !== 0) {
          if (state.url.startsWith('/settings/user')) return of(true);

          const isFeedBackProcessAc = routes.some(r => this.permittedRoutesForManagerOrManagee.includes(r));
          const isProcessOrAc = routes.some(r => this.permittedRoutesForObserver.includes(r));

          return of(
            (userRole === RoleName.observer && isProcessOrAc) || isFeedBackProcessAc || this.router.parseUrl('/home')
          );
        }

        if (route?.data?.['checkMinRole']) {
          const authorized = RoleService.minRoleName[route.data['checkMinRole']](userRole);
          if (!authorized) {
            // This will reset all state.
            this.router.navigateByUrl('');
            return of(false);
          }
        }

        return of(true);
      })
    );
  }

  canActivateChild(route: ActivatedRouteSnapshot): UrlTree | boolean | Observable<UrlTree | boolean> {
    return this.store.select(selectCurrentUser).pipe(
      take(1),
      switchMap(user => {
        const userRole = user?.roleCode ? this.getUserRoleName(user.roleCode) : RoleName.observer;
        if (route.data?.['checkMinRole']) {
          const authorized = RoleService.minRoleName[route.data['checkMinRole']](userRole);
          if (!authorized) {
            this.router.navigateByUrl('');
            return of(false);
          }
        }
        return of(true);
      })
    );
  }

  private getUserRoleName(roleCode: RoleCode): RoleName {
    return RoleService.roleName(roleCode).toLowerCase() as RoleName;
  }
}
